import { useState, useMemo, ReactNode } from 'react'

interface FilterableOption {
  label: string | ReactNode
  value: unknown
}

interface UseLocalFilteredOptionsProps<T extends FilterableOption> {
  options: T[]
  enabled?: boolean
  minLength?: number
  matchMode?: 'contains' | 'startsWith'
}

export function useLocalFilteredOptions<T extends FilterableOption>({
  options,
  enabled = false,
  minLength = 1,
  matchMode = 'contains',
}: UseLocalFilteredOptionsProps<T>) {
  const [filterText, setFilterText] = useState('')

  // Pre-compute lowercase labels/values once when options change,
  // so we don't redo toLowerCase() on every keystroke for every option
  const filterIndex = useMemo(
    () =>
      options.map(opt => {
        const labelText =
          typeof opt.label === 'string' ? opt.label : String(opt.value)
        return {
          lowerLabel: labelText.toLowerCase(),
          lowerValue: String(opt.value).toLowerCase(),
        }
      }),
    [options]
  )

  const filteredOptions = useMemo(() => {
    if (!enabled || filterText.length < minLength) return options

    const lowerFilter = filterText.toLowerCase()
    return options.filter((_opt, i) => {
      const { lowerLabel, lowerValue } = filterIndex[i]

      if (matchMode === 'startsWith') {
        return (
          lowerLabel.startsWith(lowerFilter) ||
          lowerValue.startsWith(lowerFilter)
        )
      }
      return (
        lowerLabel.includes(lowerFilter) || lowerValue.includes(lowerFilter)
      )
    })
  }, [options, filterIndex, enabled, filterText, minLength, matchMode])

  return {
    filterText,
    setFilterText,
    filteredOptions,
    isFiltering: enabled,
    hasNoResults:
      enabled && filteredOptions.length === 0 && filterText.length >= minLength,
  }
}
